import { memo, useMemo, useCallback, Dispatch, SetStateAction } from 'react';
import Path from 'path-to-regexp';

import {
  CustomLocation,
  CustomParams,
  ROUTE_WORKFLOW_OVERVIEW
} from '@/constant/routes';

import { TRAY_PORTAL_RIGHT } from '@/constant/htmlIds';

import { DataImporter } from '@/components/ui/dataImporter/dataImporter';
import { DataEditor } from '@/components/ui/dataEditor/dataEditor';
import { Widget } from '@/components/ui/widget';
import { Footer } from '@/components/layouts';
import { WorkflowContext } from '@/utils/workflowContext';
import { useToggle } from '@/utils/hooks';

import { Button } from '@/shadcn/button';
import { TraceAction, WorkflowContextType } from '@/types/workflow.types';
import { useHistory, useLocation, useParams } from 'react-router';
import { SetFunctionAsState } from '@/utils/hooks/useFunctionAsState';
import { Tray } from '@/components/ui/tray/tray';

type Props = {
  action: TraceAction;
  workflowContext: WorkflowContextType;
  traces: any[];
  toggleSignDialog: () => void;
  disabled: boolean;
  setFormData: Dispatch<SetStateAction<Record<string, unknown>>>;
  setCleanupFn: SetFunctionAsState;
};

export const DataTool = memo(
  ({
    action,
    workflowContext,
    traces,
    toggleSignDialog,
    disabled,
    setFormData,
    setCleanupFn
  }: Props) => {
    const location = useLocation<CustomLocation>();
    const history = useHistory<CustomLocation>();
    const params = useParams<CustomParams>();

    const [showTraceInfoTray, toggleShowTraceInfoTray] = useToggle(false);

    const goBack = useCallback(() => {
      if (location.state && location.state.from) {
        history.push(location.state.from);
        return;
      }
      // by default go back to workflow overview
      history.push(Path.compile(ROUTE_WORKFLOW_OVERVIEW)({ id: params.wfid }));
    }, [params, location]);

    const handleDataToolSubmit = useCallback(
      (data, fn) => {
        setFormData(data);
        setCleanupFn(fn);
        toggleSignDialog();
      },
      [setFormData, setCleanupFn, toggleSignDialog]
    );

    const { rowId: workflowRowId } = workflowContext;
    const dataToolCachingKey = useMemo(
      () =>
        !action.dataEditor
          ? `dataImporter_${workflowRowId}_${location.search}`
          : `dataEditor_${workflowRowId}_${location.search}`,
      [action, workflowRowId, location]
    );

    if (traces.length !== 1) {
      throw new Error(
        [
          'Data tool actions are only available for a single trace.',
          `${action.title || 'this action'} is not allowed for ${
            traces.length === 0 ? 'trace initialization' : 'batch update'
          }.`
        ].join('\n')
      );
    }

    const [trace] = traces;
    const traceInfoViewer = useMemo(
      () =>
        !trace.workflow.config.info
          ? undefined
          : {
              showTraceInfoTray,
              toggleShowTraceInfoTray
            },
      [showTraceInfoTray, trace]
    );

    return (
      <WorkflowContext.Provider value={workflowContext}>
        <div className="bg-background top-subnavbar relative flex h-[calc(100vh-var(--spacing-full-navbar))] w-full flex-col flex-nowrap justify-between">
          {!action.dataEditor ? (
            <DataImporter
              config={action.dataImporter}
              onSubmit={handleDataToolSubmit}
              onCancel={goBack}
              submitDisabled={disabled}
              cachedUpdatesKey={dataToolCachingKey}
              traceInfoViewer={traceInfoViewer}
            />
          ) : (
            <DataEditor
              data={trace.state}
              config={action.dataEditor}
              onSubmit={handleDataToolSubmit}
              onCancel={goBack}
              submitDisabled={disabled}
              cachedUpdatesKey={dataToolCachingKey}
              traceInfoViewer={traceInfoViewer}
            />
          )}
        </div>
        {trace.workflow.config.info && showTraceInfoTray && (
          <Tray
            portalEl={document.getElementById(TRAY_PORTAL_RIGHT)}
            title="**Trace info** viewer"
            onClose={toggleShowTraceInfoTray}
            minWidth={450}
          >
            <div className="text-foreground flex h-full flex-col flex-nowrap text-sm">
              <div className="grow overflow-auto">
                <div className="p-2.5">
                  <Widget
                    widget={trace.workflow.config.info}
                    data={trace.state}
                    links={trace?.links?.nodes}
                  />
                </div>
              </div>
              <div className="flex h-18 w-full shrink-0 flex-row flex-nowrap items-center justify-between p-3.5">
                <div className="flex flex-row flex-nowrap items-center justify-start">
                  <Button
                    variant="outline"
                    onClick={toggleShowTraceInfoTray}
                    aria-label="Close the Trace info panel"
                    type="button"
                    data-cy="trace-info-tray-close-button"
                  >
                    CLOSE
                  </Button>
                </div>
              </div>
            </div>
          </Tray>
        )}
        <Footer />
      </WorkflowContext.Provider>
    );
  }
);
